/**
 * Typed fetch wrapper for the lobby API.
 *
 * In the container the app server serves the SPA and proxies /api and /health
 * to the Go API behind a session cookie, adding X-API-Key itself, so the browser
 * never holds the key. The runtime override exists for a dev client pointed at
 * a remote API, where the key has to come from somewhere.
 */

/** Fired when the server refuses a request for lack of a session. */
export const UNAUTHORIZED_EVENT = 'dlm:unauthorized';

export type RuntimeConnection = {
  baseUrl: string;
  apiKey: string;
};

const STORAGE_BASE_URL = 'runtimeApiBaseUrl';
const STORAGE_API_KEY = 'runtimeApiKey';

/** ApiError carries the API's own error message, which is what users need. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function readConnection(): RuntimeConnection | null {
  const storage = safeStorage();
  const baseUrl = storage?.getItem(STORAGE_BASE_URL)?.trim();
  const apiKey = storage?.getItem(STORAGE_API_KEY)?.trim();
  if (!baseUrl || !apiKey) return null;
  return { baseUrl, apiKey };
}

export function writeConnection(connection: RuntimeConnection | null): void {
  const storage = safeStorage();
  if (!storage) return;
  if (!connection) {
    storage.removeItem(STORAGE_BASE_URL);
    storage.removeItem(STORAGE_API_KEY);
    return;
  }
  storage.setItem(STORAGE_BASE_URL, connection.baseUrl.replace(/\/+$/, ''));
  storage.setItem(STORAGE_API_KEY, connection.apiKey);
}

/** Extracts the most useful message an API response can offer. */
export function messageFromBody(body: unknown, fallback: string): string {
  if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>;
    for (const key of ['error', 'message'] as const) {
      const value = record[key];
      if (typeof value === 'string' && value.trim()) return value;
    }
  }
  if (typeof body === 'string' && body.trim()) return body;
  return fallback;
}

export type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  idempotencyKey?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
};

function buildUrl(path: string, query: RequestOptions['query']): string {
  const connection = readConnection();
  const base = connection ? connection.baseUrl : '';
  const url = `${base}${path}`;

  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const queryString = params.toString();
  return queryString ? `${url}?${queryString}` : url;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal, idempotencyKey } = options;
  const connection = readConnection();

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (connection) headers['X-API-Key'] = connection.apiKey;
  if (idempotencyKey !== undefined) headers['Idempotency-Key'] = idempotencyKey;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
      signal,
    });
  } catch (cause) {
    // A network-level failure has no status and no body; say so plainly rather
    // than surfacing "Failed to fetch".
    throw new ApiError('Cannot reach the lobby API', 0, cause);
  }

  const text = await response.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  // The proxy rejects an expired or missing session; tell the app so it can
  // return to the sign-in screen instead of showing an error on every panel.
  if (response.status === 401 && !connection && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
  }

  if (!response.ok) {
    throw new ApiError(
      messageFromBody(parsed, `${response.status} ${response.statusText}`),
      response.status,
      parsed,
    );
  }

  return parsed as T;
}

export const api = {
  get: <T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'POST', body }),
};
